import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { BillingSummary } from '../../client';
import { useApi } from '../../state/ApiContext';
import AddFundsModal from '../../billing/AddFundsModal';

interface ComposerFundsNoticeProps {
  teamId: string;
}

/** Floor for the "low funds" warning, unless auto-renew's minimum is higher. */
const LOW_FUNDS_FLOOR_USD = 10;

type NoticeVariant = 'no-funds' | 'budget' | 'low-funds';

interface Notice {
  variant: NoticeVariant;
  message: string;
  linkLabel: string;
}

/**
 * Decide which preemptive notice (if any) to show above a composer, from a
 * billing summary. Precedence reflects severity: agents fully stopped for
 * lack of money first, then stopped by a budget, then a low-balance warning.
 */
export function fundsNotice(summary: BillingSummary): Notice | null {
  const available = summary.availableUsd ?? 0;
  const lowThreshold = Math.max(
    LOW_FUNDS_FLOOR_USD,
    summary.autoRenewMinBalanceUsd ?? 0,
  );
  const budgetExhausted = (summary.budgets ?? []).some(
    (b) => b.amountUsd > 0 && b.spentUsd + b.reservedUsd >= b.amountUsd,
  );

  if (available <= 0) {
    return {
      variant: 'no-funds',
      message: 'No funds remaining.',
      linkLabel: 'Add funds',
    };
  }
  if (budgetExhausted) {
    return {
      variant: 'budget',
      message: 'Agents are paused by a budget limit.',
      linkLabel: 'Manage budgets',
    };
  }
  if (available < lowThreshold) {
    return {
      variant: 'low-funds',
      message: 'Low funds remaining.',
      linkLabel: 'Add funds',
    };
  }
  return null;
}

/**
 * Preemptive strip rendered just above a message/comment composer when the
 * team is out of funds, low on funds, or has agents paused by a budget.
 * Users (and other humans) can still post messages — agents simply stay
 * paused until funds are added or budgets raised — so this is informational
 * and never disables the composer. All three states link to the team's
 * billing page, where funds and budgets are managed.
 */
const ComposerFundsNotice: React.FC<ComposerFundsNoticeProps> = ({
  teamId,
}) => {
  const { billingApi } = useApi();
  const [notice, setNotice] = useState<Notice | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const loadSummary = useCallback(() => {
    const teamIdNum = parseInt(teamId, 10);
    if (Number.isNaN(teamIdNum) || !billingApi?.apiTeamTeamIdBillingGet) return;
    Promise.resolve(billingApi.apiTeamTeamIdBillingGet(teamIdNum))
      .then((res) => setNotice(fundsNotice(res.data)))
      .catch((err) => {
        // Billing being unavailable should never break the composer.
        console.error(
          'Error loading billing summary for composer notice:',
          err,
        );
      });
  }, [billingApi, teamId]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  // Keep rendering while the modal is open even after funds are added and the
  // strip itself would hide — otherwise the refresh (onFundsAdded → notice
  // becomes null) unmounts the modal and its success confirmation mid-flow.
  if (!notice && !modalOpen) return null;

  // Adding funds can't clear a budget cap, so that variant still routes to the
  // billing page; the fund-related variants add funds inline via the modal.
  const cta =
    notice?.variant === 'budget' ? (
      <Link to={`/console/team/${teamId}/billing`}>{notice.linkLabel}</Link>
    ) : (
      <button
        type="button"
        className="composer-funds-notice-cta"
        data-testid="composer-funds-notice-cta"
        onClick={() => setModalOpen(true)}>
        {notice?.linkLabel}
      </button>
    );

  return (
    <>
      {notice && (
        <div
          className={`composer-funds-notice composer-funds-notice-${notice.variant}`}
          data-testid="composer-funds-notice"
          data-variant={notice.variant}>
          {notice.message} {cta}
        </div>
      )}
      <AddFundsModal
        teamId={teamId}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onFundsAdded={loadSummary}
      />
    </>
  );
};

export default ComposerFundsNotice;
